import React, { ReactElement } from 'react';
import { IconButton, Link, styled, Tooltip, Typography } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { User } from '../../../data/models/user.model';
import StatusIndicator from '../../StatusIndicator';
import RolesDisplay from './RolesDisplay';
import { FULLSTORY_MASK_CLASSNAME } from '../../../services/analyticsUtils';
import { buildColumnVisibilityModel } from '../../../services/dataGridUtils';

const PREFIX = 'OnboardingUserDataTable';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const StyledBox = styled('div')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    marginTop: theme.spacing(3),
  },
  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

interface OnboardingUserDataTableProps {
  users: User[];
  loading: boolean;
  onDelete?: (id: number, email: string) => void;
  onSendActivationEmail?: (id: number, email: string) => void;
}

export default function OnboardingUserDataTable({
  users,
  loading,
  onDelete,
  onSendActivationEmail,
}: OnboardingUserDataTableProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const columns = [
    { field: 'userAccountID', headerName: 'ID', hide: true },
    {
      field: 'userLastName',
      headerName: t('users.lastName'),
      flex: 1,
      sortable: false,
      cellClassName: FULLSTORY_MASK_CLASSNAME,
      renderCell: (params: GridCellParams): ReactElement => {
        return (
          <Link
            href="#"
            onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              evt.preventDefault();
              navigate('/users/' + params.row.userAccountID);
            }}
            color="textPrimary"
            underline="always">
            {params?.row?.userLastName}
          </Link>
        );
      },
    },
    {
      field: 'userFirstName',
      headerName: t('users.firstName'),
      flex: 1,
      sortable: false,
      cellClassName: FULLSTORY_MASK_CLASSNAME,
    },
    {
      field: 'emailAddress',
      headerName: t('users.email'),
      flex: 2,
      sortable: false,
      cellClassName: FULLSTORY_MASK_CLASSNAME,
    },
    {
      field: 'userAccountRoles',
      headerName: t('users.roles'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridCellParams): ReactElement => (
        <RolesDisplay roles={params?.row?.userAccountRoles} />
      ),
    },
    {
      field: 'userAccountState',
      headerName: t('common.active'),
      width: 80,
      renderCell: (params: GridCellParams) => <StatusIndicator cell={params} />,
    },
    {
      field: 'Actions',
      headerName: ' ',
      width: 120,
      hide: !onSendActivationEmail && !onDelete,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            {onSendActivationEmail && (
              <Tooltip title={t('users.sendActivation')}>
                <IconButton
                  data-testid="reset"
                  onClick={() =>
                    onSendActivationEmail(
                      params.row.userAccountID,
                      params.row.emailAddress,
                    )
                  }
                  size="large"
                  color="default">
                  <ForwardToInboxIcon />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title={t('users.deleteUser')}>
                <IconButton
                  data-testid="delete"
                  onClick={() =>
                    onDelete(params.row.userAccountID, params.row.emailAddress)
                  }
                  size="large"
                  color="primary">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
      sortable: false,
    },
  ];

  const columnVisibilityModel = React.useMemo(
    () => buildColumnVisibilityModel(columns as any),
    [onSendActivationEmail, onDelete],
  );

  return (
    <StyledBox data-testid="onboarding-table" className={classes.container}>
      {users ? (
        <StyledBox>
          <Typography variant="h5" align="center">
            {t('users.onboardingUsers')}
          </Typography>
          <DataGridPro
            getRowId={(row) => row.userAccountID!}
            rows={users}
            columns={columns as any}
            rowCount={users.length}
            hideFooter={true}
            autoHeight={true}
            loading={loading}
            className={classes.grid}
            checkboxSelection={false}
            disableColumnMenu={true}
            disableColumnSelector={true}
            getRowHeight={() => 'auto'}
            initialState={{ columns: { columnVisibilityModel } }}
          />
        </StyledBox>
      ) : null}
    </StyledBox>
  );
}
